import * as THREE from 'three'

/**
 * «Кирпич» — товар без 3D-модели (категории «Прямые комплекты памятников» и т.п.),
 * который сцена строит сама по размеру из названия: «… — 100×50×8» (см: высота × ширина × толщина).
 * Сервер помечает такие товары полем procedural: { type: 'brick', heightMm, widthMm, thicknessMm, polish, chamferMm }.
 *
 * Оси сцены (Z — вверх, как у STL-памятников): X — ширина, Y — толщина, Z — высота.
 * Сцена ставит такие модели низом на пол/подставку (центр по X/Y, низ по Z).
 * Стороны: 0 право(+X), 1 лево(-X), 2 зад(+Y), 3 перед(-Y), 4 верх(+Z), 5 низ(-Z).
 * Имена материалов «Полированная…/Неполированная…» — по ним сцена сама кладёт гранит и неполированную поверхность.
 */

export const BRICK_POLISH_FACES = Object.freeze({
    five: [0, 1, 2, 3, 4], // 5 ст. — всё, кроме низа
    two: [2, 3], // 2 ст. — перед и зад
    front: [3], // 1 ст. — только перед
    top: [4], // 1 ст. — только верх (эконом)
    all: [0, 1, 2, 3, 4, 5], // круговая
    none: [],
})

const FACE_NAMES = ['право', 'лево', 'зад', 'перед', 'верх', 'низ']

export const isBrickProduct = (product) => product?.procedural?.type === 'brick'

/**
 * Коробка с 6 группами (0 +X, 1 −X, 2 +Y, 3 −Y, 4 +Z, 5 −Z). Фаска — как у цветника и подставки:
 * плоские срезы по всем рёбрам внутрь габарита (не больше четверти полутолщины),
 * у 5 ст. срез полируется вместе с соседней стороной, у 1 ст. и 2 ст. — нет.
 */
export const createBrickGeometry = ({ x, y, z }, chamferM = 0, polishedFaces = [], chamferPolished = true) => {
    const half = [x / 2, y / 2, z / 2]
    const cut = Math.min(Math.max(0, chamferM), ...half.map((value) => value * 0.24))
    if (!(cut > 0)) {
        const box = new THREE.BoxGeometry(x, y, z)
        box.computeBoundingBox()
        box.computeBoundingSphere()
        return box
    }
    const inner = half.map((value) => value - cut)
    const positions = []
    const uvs = []
    const geometry = new THREE.BufferGeometry()
    const polished = new Set(polishedFaces)
    const faceIndex = (axis, sign) => axis * 2 + (sign > 0 ? 0 : 1)
    // Срез полируется только у камня 5 ст.; у 1 ст. и 2 ст. срезы неполированные.
    // Срез полированный, только если камень 5 ст. и ВСЕ соседние стороны полированные.
    const materialFor = (indices) => (chamferPolished && indices.every((index) => polished.has(index))
        ? indices[0]
        : (indices.find((index) => !polished.has(index)) ?? 5))
    const polygon = (points, materialIndex) => {
        const edgeA = points[1].map((value, axis) => value - points[0][axis])
        const edgeB = points[2].map((value, axis) => value - points[0][axis])
        const normal = [
            edgeA[1] * edgeB[2] - edgeA[2] * edgeB[1],
            edgeA[2] * edgeB[0] - edgeA[0] * edgeB[2],
            edgeA[0] * edgeB[1] - edgeA[1] * edgeB[0],
        ]
        const center = [0, 1, 2].map((axis) => points.reduce((sum, point) => sum + point[axis], 0) / points.length)
        if (normal.reduce((sum, value, axis) => sum + value * center[axis], 0) < 0) points.reverse()
        const dominant = normal.map(Math.abs).indexOf(Math.max(...normal.map(Math.abs)))
        const axes = [0, 1, 2].filter((axis) => axis !== dominant)
        const start = positions.length / 3
        for (let index = 1; index < points.length - 1; index += 1) {
            ;[points[0], points[index], points[index + 1]].forEach((point) => {
                positions.push(...point)
                uvs.push(point[axes[0]] / (2 * half[axes[0]]) + 0.5, point[axes[1]] / (2 * half[axes[1]]) + 0.5)
            })
        }
        geometry.addGroup(start, positions.length / 3 - start, materialIndex)
    }
    for (let axis = 0; axis < 3; axis += 1) {
        const other = [0, 1, 2].filter((value) => value !== axis)
        for (const sign of [-1, 1]) polygon([[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([a, b]) => {
            const point = [0, 0, 0]
            point[axis] = sign * half[axis]
            point[other[0]] = a * inner[other[0]]
            point[other[1]] = b * inner[other[1]]
            return point
        }), faceIndex(axis, sign))
    }
    for (let a = 0; a < 3; a += 1) for (let b = a + 1; b < 3; b += 1) {
        const c = 3 - a - b
        for (const sa of [-1, 1]) for (const sb of [-1, 1]) {
            const point = (outerA, along) => {
                const value = [0, 0, 0]
                value[a] = sa * (outerA ? half[a] : inner[a])
                value[b] = sb * (outerA ? inner[b] : half[b])
                value[c] = along * inner[c]
                return value
            }
            polygon([point(true, -1), point(false, -1), point(false, 1), point(true, 1)], materialFor([faceIndex(a, sa), faceIndex(b, sb)]))
        }
    }
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) {
        const signs = [sx, sy, sz]
        polygon([0, 1, 2].map((outerAxis) => signs.map((sign, axis) => sign * (axis === outerAxis ? half[axis] : inner[axis]))), materialFor(signs.map((sign, axis) => faceIndex(axis, sign))))
    }
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2))
    geometry.computeVertexNormals()
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    return geometry
}

/** Реальный размер камня на сцене, м: X — ширина, Y — толщина, Z — высота (двойник — боком). */
export const getBrickSizeMeters = (procedural = {}) => {
    const p = procedural || {}
    const width = Math.max(10, Number(p.widthMm) || 400) / 1000
    const height = Math.max(10, Number(p.heightMm) || 800) / 1000
    // Двойник — тот же камень боком: длинная сторона по горизонтали, низ — бывший бок (неполированный).
    return {
        x: p.double ? Math.max(width, height) : width,
        y: Math.max(5, Number(p.thicknessMm) || 50) / 1000,
        z: p.double ? Math.min(width, height) : height,
    }
}

/** Данные для modelsData (как после загрузки GLB). */
export const buildBrickModelData = (product = {}) => {
    const p = product.procedural || {}
    const { x, y, z } = getBrickSizeMeters(p)
    // У кирпича только 5 ст. / 2 ст. / 1 ст.; остальное (старые значения) — как 5 ст.
    const polishKey = ['five', 'two', 'front'].includes(p.polish) ? p.polish : 'five'
    const polished = new Set(BRICK_POLISH_FACES[polishKey])
    const geometry = createBrickGeometry({ x, y, z }, Math.max(0, Number(p.chamferMm) || 0) / 1000, [...polished], polishKey === 'five')
    // Низ камня — в нуле: стоит на подставке/полу, а не уходит в неё наполовину.
    geometry.translate(0, 0, z / 2)
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    // Всего ДВА материала: 0 — полированные стороны, 1 — неполированные.
    // Выбор гранита один на весь камень; неполированные стороны сцена кладёт сама.
    geometry.groups.forEach((group) => {
        group.materialIndex = polished.has(group.materialIndex) ? 0 : 1
    })
    const polishedFaces = FACE_NAMES.filter((_, index) => polished.has(index))
    const roughFaces = FACE_NAMES.filter((_, index) => !polished.has(index))
    const polishedMaterial = new THREE.MeshStandardMaterial({ color: 0x1c1c1e, roughness: 0.18, metalness: 0 })
    polishedMaterial.name = `Полированная поверхность${polishedFaces.length ? ` (${polishedFaces.join(', ')})` : ''}`
    const roughMaterial = new THREE.MeshStandardMaterial({ color: 0x8d8f93, roughness: 0.88, metalness: 0 })
    roughMaterial.name = `Неполированная поверхность${roughFaces.length ? ` (${roughFaces.join(', ')})` : ''}`
    const materials = [polishedMaterial, roughMaterial]
    return {
        geometry,
        materials,
        dimensions: { x: x.toFixed(3), y: y.toFixed(3), z: z.toFixed(3) },
        // Z-вверх без поворота: сцена ставит низом на подставку, как STL-памятники.
        fileType: 'stl',
    }
}
